import { toast } from "react-toastify";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import livestockService from '../../../shared/api/animal/livestockService';

// ---------- HELPERS ----------
const safeNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatKg = (value, digits = 1) => {
  const n = safeNumber(value);
  return n === null ? '—' : `${n.toFixed(digits)} kg`;
};

const formatMoney = (value) => {
  const n = safeNumber(value);
  return n === null ? '—' : `₹${n.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const daysAgoLabel = (value) => {
  if (!value) return 'Not recorded';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const oneDay = 24 * 60 * 60 * 1000;
  const diff = Math.max(0, Math.floor((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / oneDay));

  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

const toTitle = (value) => {
  if (!value) return '—';
  return value
    .toString()
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.message) return data.message;
  return error?.message || fallback;
};

const getTrendClass = (trend) => {
  switch ((trend || '').toUpperCase()) {
    case 'GAIN':
      return 'trend gain';
    case 'LOSS':
      return 'trend loss';
    case 'STABLE':
      return 'trend stable';
    case 'FIRST_RECORD':
      return 'trend first';
    default:
      return 'trend muted';
  }
};

const renderChangeText = (value, trend) => {
  const n = safeNumber(value);
  if (n === null) {
    if ((trend || '').toUpperCase() === 'FIRST_RECORD') return 'First record';
    return 'No change data';
  }
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)} kg`;
};

const sortWeightRowsAsc = (rows = []) =>
  [...rows].sort((a, b) => {
    const da = new Date(`${a.measuredAt}T00:00:00`).getTime();
    const db = new Date(`${b.measuredAt}T00:00:00`).getTime();
    if (da !== db) return da - db;
    return (a.id || 0) - (b.id || 0);
  });

const sortWeightRowsDesc = (rows = []) =>
  [...rows].sort((a, b) => {
    const da = new Date(`${a.measuredAt}T00:00:00`).getTime();
    const db = new Date(`${b.measuredAt}T00:00:00`).getTime();
    if (da !== db) return db - da;
    return (b.id || 0) - (a.id || 0);
  });

// ---------- MODERN STYLES ----------
const Styles = () => (
  <style>{`
    .page-container {
      color: #F7FAFB;
      font-family: 'Inter', system-ui, -apple-system, sans-serif;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .page-title {
      font-size: 1.875rem;
      font-weight: 700;
      margin: 0;
      background: linear-gradient(135deg, #F7FAFC 0%, #A0AEC0 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }

    .breadcrumb a {
      color: #64748B;
      text-decoration: none;
      font-size: 0.875rem;
      transition: color 0.2s ease;
    }

    .breadcrumb a:hover { color: #6366F1; }
    .breadcrumb span { color: #475569; }

    .btn {
      padding: 0.625rem 1.25rem;
      border-radius: 10px;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.2s ease;
      font-size: 0.875rem;
      border: none;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }

    .btn-primary {
      background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
      color: white;
      box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
    }

    .btn-primary:hover {
      background: linear-gradient(135deg, #4F46E5 0%, #4338CA 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
    }

    .btn-secondary {
      background: rgba(45, 55, 72, 0.8);
      color: #E2E8F0;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn-secondary:hover {
      background: rgba(55, 65, 81, 0.9);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .btn-danger {
      background: rgba(239, 68, 68, 0.15);
      color: #F87171;
      border: 1px solid rgba(239, 68, 68, 0.25);
    }

    .btn-danger:hover {
      background: rgba(239, 68, 68, 0.22);
    }

    .bulk-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.9rem 1rem;
      margin-bottom: 1rem;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(30, 37, 54, 0.62);
      flex-wrap: wrap;
    }

    .bulk-count {
      color: #CBD5E1;
      font-weight: 800;
      font-size: 0.9rem;
    }

    .bulk-actions {
      display: flex;
      align-items: center;
      gap: 0.65rem;
      flex-wrap: wrap;
    }

    .select-checkbox {
      width: 18px;
      height: 18px;
      accent-color: #6366F1;
      cursor: pointer;
    }

    .table-container {
      background: linear-gradient(145deg, #1E2536 0%, #1A1F2E 100%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      overflow-x: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

    .data-table {
      width: 100%;
      border-collapse: collapse;
    }

    .data-table th, .data-table td {
      padding: 1rem 1.25rem;
      text-align: left;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      white-space: nowrap;
      vertical-align: middle;
    }

    .data-table th {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #64748B;
      font-weight: 600;
      background: rgba(30, 37, 54, 0.5);
    }

    .data-table td {
      font-weight: 500;
      color: #E2E8F0;
    }

    .data-table tbody tr {
      transition: all 0.15s ease;
    }

    .data-table tbody tr:hover {
      background: rgba(99, 102, 241, 0.08);
    }

    .data-table tr:last-child td {
      border-bottom: none;
    }

    .cell-stack {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
    }

    .primary-text {
      color: #F8FAFC;
      font-weight: 600;
    }

    .secondary-text {
      color: #94A3B8;
      font-size: 0.78rem;
      font-weight: 500;
    }

    .weight-main {
      color: #F8FAFC;
      font-weight: 700;
      font-size: 0.95rem;
    }

    .weight-subline {
      margin-top: 0.2rem;
      display: flex;
      align-items: center;
      gap: 0.45rem;
      font-size: 0.78rem;
      flex-wrap: wrap;
    }

    .record-pill {
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.08);
      color: #CBD5E1;
      padding: 0.12rem 0.5rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 600;
    }

    .badge {
      padding: 0.35rem 0.85rem;
      border-radius: 999px;
      font-weight: 700;
      font-size: 0.75rem;
      text-transform: capitalize;
      display: inline-block;
      border: 1px solid rgba(255,255,255,0.12);
    }

    .badge.sold { background: rgba(239, 68, 68, 0.15); color: #F87171; border-color: rgba(239, 68, 68, 0.3); }
    .badge.available { background: rgba(16, 185, 129, 0.15); color: #34D399; border-color: rgba(16, 185, 129, 0.3); }
    .badge.culled { background: rgba(245, 158, 11, 0.15); color: #FBBF24; border-color: rgba(245, 158, 11, 0.3); }
    .badge.deceased { background: rgba(100, 116, 139, 0.18); color: #CBD5E1; border-color: rgba(100, 116, 139, 0.35); }

    .badge.healthy { background: rgba(16, 185, 129, 0.12); color: #34D399; border-color: rgba(16, 185, 129, 0.28); }
    .badge.sick { background: rgba(239, 68, 68, 0.12); color: #F87171; border-color: rgba(239, 68, 68, 0.28); }
    .badge.in_treatment { background: rgba(59, 130, 246, 0.12); color: #60A5FA; border-color: rgba(59, 130, 246, 0.28); }
    .badge.recovering { background: rgba(245, 158, 11, 0.12); color: #FBBF24; border-color: rgba(245, 158, 11, 0.28); }
    .badge.quarantined { background: rgba(168, 85, 247, 0.12); color: #C084FC; border-color: rgba(168, 85, 247, 0.28); }
    .badge.injured { background: rgba(244, 63, 94, 0.12); color: #FB7185; border-color: rgba(244, 63, 94, 0.28); }
    .badge.dead { background: rgba(100, 116, 139, 0.12); color: #94A3B8; border-color: rgba(100, 116, 139, 0.28); }

    .trend {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      font-weight: 700;
      font-size: 0.78rem;
    }

    .trend.gain { color: #34D399; }
    .trend.loss { color: #F87171; }
    .trend.stable { color: #FBBF24; }
    .trend.first { color: #A78BFA; }
    .trend.muted { color: #94A3B8; }

    .action-buttons {
      display: flex;
      gap: 0.4rem;
    }

    .action-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .action-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: scale(1.05);
    }

    .action-btn svg {
      width: 18px;
      height: 18px;
    }

    .action-btn.edit svg { color: #60A5FA; }
    .action-btn.delete svg { color: #F87171; }
    .action-btn.weight svg { color: #A78BFA; }
    .action-btn.history svg { color: #FBBF24; }
    .action-btn.sold svg { color: #34D399; }

    .modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.75);
      backdrop-filter: blur(8px);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
      padding: 1rem;
      animation: fadeIn 0.2s ease-out;
    }

    .modal-content {
      background: linear-gradient(145deg, #1E2536 0%, #1A1F2E 100%);
      color: #F7FAFC;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 1.75rem;
      width: 100%;
      max-width: 640px;
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
      animation: slideUp 0.3s ease-out;
      max-height: calc(100vh - 2rem);
      overflow: auto;
    }

    .modal-content.large {
      max-width: 1080px;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1.25rem;
      gap: 1rem;
    }

    .modal-title {
      font-size: 1.25rem;
      font-weight: 700;
      margin: 0;
    }

    .modal-subtitle {
      color: #94A3B8;
      font-size: 0.85rem;
      margin-top: 0.25rem;
    }

    .close-btn {
      background: rgba(255, 255, 255, 0.05);
      border: none;
      color: #64748B;
      font-size: 1.75rem;
      cursor: pointer;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
    }

    .close-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #F7FAFC;
      transform: rotate(90deg);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
    }

    .form-group { margin-bottom: 0.5rem; }
    .form-group.full { grid-column: 1 / -1; }

    .form-group label {
      display: block;
      font-size: 0.875rem;
      color: #94A3B8;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }

    .form-input, .form-select, .form-textarea {
      width: 100%;
      background: rgba(30, 37, 54, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      padding: 0.75rem 1rem;
      color: #F7FAFC;
      font-size: 0.95rem;
      transition: all 0.2s ease;
    }

    .form-input:focus, .form-select:focus, .form-textarea:focus {
      outline: none;
      border-color: #6366F1;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    }

    .form-input::placeholder, .form-textarea::placeholder {
      color: #64748B;
    }

    .helper-text {
      margin-top: 0.45rem;
      color: #94A3B8;
      font-size: 0.78rem;
      line-height: 1.45;
    }

    .form-checkbox {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding-top: 0.35rem;
      font-size: 0.875rem;
      color: #94A3B8;
    }

    .form-checkbox input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #6366F1;
    }

    .feedback-container {
      text-align: center;
      padding: 4rem 2rem;
      color: #64748B;
    }

    .message-box {
      padding: 0.9rem 1rem;
      border-radius: 12px;
      margin-bottom: 1rem;
      font-size: 0.875rem;
      font-weight: 600;
    }

    .message-box.error {
      background: rgba(239, 68, 68, 0.12);
      color: #FCA5A5;
      border: 1px solid rgba(239, 68, 68, 0.2);
    }

    .message-box.warning {
      background: rgba(245, 158, 11, 0.12);
      color: #FCD34D;
      border: 1px solid rgba(245, 158, 11, 0.2);
    }

    .summary-strip {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 0.85rem;
      margin-bottom: 1.2rem;
    }

    .summary-card {
      background: rgba(15, 20, 25, 0.58);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 14px;
      padding: 0.95rem 1rem;
      min-width: 0;
    }

    .summary-label {
      font-size: 0.75rem;
      color: #94A3B8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }

    .summary-value {
      font-size: 1rem;
      font-weight: 800;
      color: #F8FAFC;
      line-height: 1.25;
    }

    .summary-meta {
      margin-top: 0.35rem;
      font-size: 0.78rem;
      color: #94A3B8;
      line-height: 1.4;
    }

    .quick-context {
      display: flex;
      flex-wrap: wrap;
      gap: 0.65rem;
      margin-bottom: 1rem;
    }

    .quick-pill {
      padding: 0.5rem 0.75rem;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #CBD5E1;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .history-layout {
      display: grid;
      grid-template-columns: 1.15fr 1fr;
      gap: 1.2rem;
      margin-top: 1rem;
    }

    .panel {
      background: rgba(15, 20, 25, 0.58);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 16px;
      padding: 1rem;
      min-width: 0;
    }

    .panel-title {
      font-size: 0.92rem;
      font-weight: 800;
      color: #F8FAFC;
      margin: 0 0 0.9rem 0;
    }

    .chart-shell {
      border-radius: 14px;
      background: linear-gradient(180deg, rgba(12,17,24,0.7), rgba(8,12,18,0.75));
      border: 1px solid rgba(255,255,255,0.06);
      padding: 0.8rem;
      min-height: 250px;
    }

    .chart-empty {
      min-height: 250px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #64748B;
      font-size: 0.9rem;
    }

    .chart-legend {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      flex-wrap: wrap;
      margin-top: 0.75rem;
      font-size: 0.78rem;
      color: #94A3B8;
    }

    .history-table-wrap {
      max-height: 430px;
      overflow: auto;
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 12px;
    }

    .history-table {
      width: 100%;
      border-collapse: collapse;
    }

    .history-table th,
    .history-table td {
      padding: 0.85rem 0.9rem;
      border-bottom: 1px solid rgba(255,255,255,0.06);
      text-align: left;
      vertical-align: top;
      white-space: nowrap;
    }

    .history-table th {
      position: sticky;
      top: 0;
      background: #192131;
      color: #94A3B8;
      font-size: 0.74rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      z-index: 1;
    }

    .history-table td {
      color: #E2E8F0;
      font-size: 0.87rem;
    }

    .history-table tr:last-child td {
      border-bottom: none;
    }

    .history-note {
      max-width: 220px;
      white-space: normal;
      color: #CBD5E1;
      line-height: 1.45;
    }

    .mini-actions {
      display: flex;
      gap: 0.4rem;
    }

    .mini-btn {
      border: none;
      border-radius: 8px;
      cursor: pointer;
      padding: 0.45rem 0.6rem;
      font-size: 0.78rem;
      font-weight: 700;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }

    .mini-btn svg {
      width: 15px;
      height: 15px;
    }

    .mini-btn.edit {
      background: rgba(59, 130, 246, 0.12);
      color: #93C5FD;
      border: 1px solid rgba(59, 130, 246, 0.18);
    }

    .mini-btn.delete {
      background: rgba(239, 68, 68, 0.12);
      color: #FCA5A5;
      border: 1px solid rgba(239, 68, 68, 0.18);
    }

    .mini-btn:hover {
      transform: translateY(-1px);
      filter: brightness(1.05);
    }

    .skeleton-bar {
      background: linear-gradient(90deg, #2D3748 25%, #3D4A5C 50%, #2D3748 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 6px;
      height: 14px;
    }

    .inline-loader {
      color: #94A3B8;
      font-size: 0.84rem;
      font-weight: 600;
    }

    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @media (max-width: 1024px) {
      .history-layout {
        grid-template-columns: 1fr;
      }
    }

    @media (max-width: 768px) {
      .form-grid { grid-template-columns: 1fr; }
      .summary-strip { grid-template-columns: 1fr 1fr; }
      .modal-content.large { padding: 1rem; }
    }

    @media (max-width: 560px) {
      .summary-strip { grid-template-columns: 1fr; }
      .page-title { font-size: 1.45rem; }
    }
  `}</style>
);

// ---------- ICONS ----------
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"/>
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166M4.772 5.79a48.108 48.108 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
  </svg>
);

const WeightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 2a1 1 0 100 2h1.18A3 3 0 0010 7h4a3 3 0 002.82-3H18a1 1 0 100-2H6z"/>
    <path fillRule="evenodd" d="M5 7a3 3 0 00-3 3v7a3 3 0 003 3h14a3 3 0 003-3v-7a3 3 0 00-3-3H5zm5.75 3.5a.75.75 0 011.5 0v6a.75.75 0 01-1.5 0v-6z" clipRule="evenodd"/>
  </svg>
);

const HistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-3.07-7.22M22 4v6h-6" />
  </svg>
);

const SoldIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ---------- CHART ----------
const WeightLineChart = ({ records }) => {
  const asc = useMemo(() => sortWeightRowsAsc(records), [records]);

  if (!asc.length) {
    return <div className="chart-empty">No weight records yet</div>;
  }

  const width = 640;
  const height = 250;
  const paddingX = 36;
  const paddingY = 24;

  const values = asc.map((r) => Number(r.weightKg));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 1);

  const points = asc.map((r, idx) => {
    const x = paddingX + (idx * ((width - paddingX * 2) / Math.max(asc.length - 1, 1)));
    const y = height - paddingY - (((Number(r.weightKg) - min) / range) * (height - paddingY * 2));
    return { ...r, x, y };
  });

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const ticks = [0, 0.5, 1].map((step) => {
    const value = max - step * range;
    const y = paddingY + step * (height - paddingY * 2);
    return { value, y };
  });

  return (
    <div className="chart-shell">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="250" role="img" aria-label="Weight trend chart">
        <defs>
          <linearGradient id="weightStroke" x1="0" x2="1">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
          <linearGradient id="weightFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(167,139,250,0.28)" />
            <stop offset="100%" stopColor="rgba(99,102,241,0.02)" />
          </linearGradient>
        </defs>

        {ticks.map((t, idx) => (
          <g key={idx}>
            <line x1={paddingX} y1={t.y} x2={width - paddingX} y2={t.y} stroke="rgba(255,255,255,0.08)" strokeDasharray="4 4" />
            <text x="4" y={t.y + 4} fill="#94A3B8" fontSize="12">
              {t.value.toFixed(1)} kg
            </text>
          </g>
        ))}

        {points.length > 1 && (
          <polygon
            points={`${points[0].x},${height - paddingY} ${polylinePoints} ${points[points.length - 1].x},${height - paddingY}`}
            fill="url(#weightFill)"
          />
        )}

        {points.length > 1 ? (
          <polyline
            fill="none"
            stroke="url(#weightStroke)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={polylinePoints}
          />
        ) : (
          <circle cx={points[0].x} cy={points[0].y} r="5" fill="#A78BFA" />
        )}

        {points.map((p) => (
          <g key={p.id}>
            <circle cx={p.x} cy={p.y} r="4.5" fill="#A78BFA" stroke="#0f172a" strokeWidth="2" />
            <text x={p.x} y={height - 6} textAnchor="middle" fill="#94A3B8" fontSize="12">
              {new Date(`${p.measuredAt}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </text>
          </g>
        ))}
      </svg>

      <div className="chart-legend">
        <span>First: {formatKg(asc[0]?.weightKg)}</span>
        <span>Latest: {formatKg(asc[asc.length - 1]?.weightKg)}</span>
        <span>{asc.length} record{asc.length !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

// ---------- MODALS ----------
const localNextAnimalId = (rows = [], type = 'GOAT') => {
  const normalized = String(type || 'GOAT').toUpperCase();
  const matcher = new RegExp(`^${normalized}[-\\s]?(\\d+)$`, 'i');
  let highest = 0;
  let width = 2;
  rows.forEach((row) => {
    const match = String(row?.animalId || '').trim().match(matcher);
    if (!match) return;
    highest = Math.max(highest, Number(match[1]) || 0);
    width = Math.max(width, match[1].length);
  });
  const prefix = normalized.charAt(0) + normalized.slice(1).toLowerCase();
  return `${prefix}-${String(highest + 1).padStart(width, '0')}`;
};

const LivestockFormModal = ({ isOpen, onClose, onSave, initialData, batchId, existingLivestock = [] }) => {
  const isEditing = !!initialData;

  const numOrZero = (v) => {
    if (v === '' || v === null || typeof v === 'undefined') return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const [formData, setFormData] = useState({});
  const [animalIdEdited, setAnimalIdEdited] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  useEffect(() => {
    setAnimalIdEdited(false);
    setFormData({
      type: initialData?.type || 'GOAT',
      animalId: initialData?.animalId || '',
      gender: initialData?.gender || (initialData ? 'UNKNOWN' : 'MALE'),
      healthStatus: initialData?.healthStatus || 'HEALTHY',
      currentStatus: initialData?.currentStatus || 'AVAILABLE',
      saleAmount: initialData?.saleAmount ?? '',
      cost: initialData?.cost ?? '',
      sold: !!(initialData?.sold || initialData?.currentStatus === 'SOLD'),
    });
  }, [initialData, isOpen]);

  useEffect(() => {
    if (!isOpen || isEditing || !formData.type || animalIdEdited) return undefined;
    let active = true;
    setSuggestionLoading(true);
    livestockService.getNextAnimalId(formData.type)
      .catch(() => localNextAnimalId(existingLivestock, formData.type))
      .then((suggestion) => {
        if (active && suggestion) setFormData((previous) => ({ ...previous, animalId: suggestion }));
      })
      .finally(() => { if (active) setSuggestionLoading(false); });
    return () => { active = false; };
  }, [isOpen, isEditing, formData.type, animalIdEdited, existingLivestock]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'animalId') setAnimalIdEdited(true);
    if (name === 'type' && !isEditing) setAnimalIdEdited(false);

    setFormData((p) => {
      const next = { ...p, [name]: type === 'checkbox' ? checked : value };

      if (name === 'type' && !isEditing) next.animalId = '';

      if (name === 'sold') {
        if (checked) next.currentStatus = 'SOLD';
        else if (next.currentStatus === 'SOLD') next.currentStatus = 'AVAILABLE';
      }

      if (name === 'currentStatus') {
        if (value === 'SOLD') next.sold = true;
        if (value !== 'SOLD' && p.sold) next.sold = false;
      }

      return next;
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    const payload = {
      batchId: Number(batchId),
      type: formData.type,
      animalId: formData.animalId?.trim() || null,
      gender: formData.gender,
      healthStatus: formData.healthStatus,
      currentStatus: formData.currentStatus,
      quantity: 1,
      weight: isEditing ? numOrZero(initialData?.weight) : 0,
      ...(isEditing ? { saleAmount: formData.saleAmount === '' ? null : Number(formData.saleAmount) } : {}),
      sold: !!formData.sold,
    };

    if (payload.animalId === null) delete payload.animalId;
    onSave(initialData?.id, payload);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{isEditing ? 'Edit Livestock' : 'Add New Livestock'}</div>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select id="type" name="type" value={formData.type} onChange={onChange} className="form-select">
                <option value="GOAT">Goat</option>
                <option value="COW">Cow</option>
                <option value="BIRD">Bird</option>
                <option value="FISH">Fish</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="animalId">Animal ID (Tag)</label>
              <input
                id="animalId"
                name="animalId"
                type="text"
                value={formData.animalId}
                onChange={onChange}
                className="form-input"
                placeholder={suggestionLoading ? 'Finding next available ID...' : 'e.g., Goat-01'}
              />
              {!isEditing && <div className="helper-text">{suggestionLoading ? 'Checking existing livestock IDs...' : 'Suggested automatically from the next available number. You can edit it if needed.'}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select id="gender" name="gender" value={formData.gender} onChange={onChange} className="form-select">
                <option value="UNKNOWN">Unknown</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="healthStatus">Health Status</label>
              <select id="healthStatus" name="healthStatus" value={formData.healthStatus} onChange={onChange} className="form-select">
                <option value="HEALTHY">Healthy</option>
                <option value="SICK">Sick</option>
                <option value="IN_TREATMENT">In Treatment</option>
                <option value="RECOVERING">Recovering</option>
                <option value="QUARANTINED">Quarantined</option>
                <option value="INJURED">Injured</option>
                <option value="DEAD">Dead</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="currentStatus">Current Status</label>
              <select id="currentStatus" name="currentStatus" value={formData.currentStatus} onChange={onChange} className="form-select">
                <option value="AVAILABLE">Available</option>
                <option value="SOLD">Sold</option>
                <option value="CULLED">Culled</option>
                <option value="DECEASED">Deceased</option>
              </select>
            </div>

            {isEditing && (formData.sold || formData.currentStatus === 'SOLD') && (
              <div className="form-group">
                <label htmlFor="saleAmount">Actual Sale Amount (₹)</label>
                <input id="saleAmount" name="saleAmount" type="number" min="0" step="0.01"
                  value={formData.saleAmount} onChange={onChange} className="form-input"
                  placeholder="Amount received for this goat" />
                <div className="helper-text">Actual revenue is recorded per goat, not estimated at batch creation.</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-checkbox">
                <input type="checkbox" name="sold" checked={!!formData.sold} onChange={onChange} />
                Mark as Sold
              </label>
            </div>
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'.75rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{isEditing ? 'Update' : 'Add'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const WeightModal = ({
  isOpen,
  onClose,
  onSave,
  animal,
  summary,
  loading,
  initialRecord,
}) => {
  const isEditing = !!initialRecord;
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [weightKg, setWeightKg] = useState('');
  const [notes, setNotes] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    if (initialRecord) {
      setDate(initialRecord.measuredAt || new Date().toISOString().split('T')[0]);
      setWeightKg(initialRecord.weightKg ?? '');
      setNotes(initialRecord.notes || '');
    } else {
      setDate(new Date().toISOString().split('T')[0]);
      setWeightKg('');
      setNotes('');
    }

    setLocalError('');
  }, [isOpen, initialRecord]);

  const referenceWeight =
    safeNumber(initialRecord?.previousWeightKg) ??
    safeNumber(summary?.currentWeightKg) ??
    safeNumber(animal?.weight);

  const liveChange = useMemo(() => {
    const entered = safeNumber(weightKg);
    if (entered === null || referenceWeight === null) return null;
    return Number((entered - referenceWeight).toFixed(2));
  }, [weightKg, referenceWeight]);

  const unusualJump = useMemo(() => {
    const entered = safeNumber(weightKg);
    if (entered === null || referenceWeight === null) return false;
    const absolute = Math.abs(entered - referenceWeight);
    const ratio = referenceWeight > 0 ? absolute / referenceWeight : 0;
    return absolute > 5 || ratio > 0.25;
  }, [weightKg, referenceWeight]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');

    const parsedWeight = safeNumber(weightKg);
    if (parsedWeight === null || parsedWeight <= 0) {
      setLocalError('Weight must be greater than 0.');
      return;
    }

    if (!date) {
      setLocalError('Measurement date is required.');
      return;
    }

    if (unusualJump) {
      const ok = window.confirm(
        `This entry changes weight by ${renderChangeText(liveChange)} from the previous value. Do you want to continue?`
      );
      if (!ok) return;
    }

    onSave(animal.id, initialRecord?.id || null, {
      weightKg: parsedWeight,
      measuredAt: date,
      notes: notes?.trim() || null,
    });
  };

  if (!isOpen || !animal) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">
              {isEditing ? 'Edit Weight Record' : 'Record Weight'} — {animal.animalId || `#${animal.id}`}
            </div>
            <div className="modal-subtitle">
              {toTitle(animal.type)} • Current shown weight: {formatKg(summary?.currentWeightKg ?? animal.weight)}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {localError && <div className="message-box error">{localError}</div>}
        {unusualJump && (
          <div className="message-box warning">
            Large weight change detected. Please verify before saving.
          </div>
        )}

        {loading ? (
          <div className="summary-strip">
            {[...Array(4)].map((_, i) => (
              <div className="summary-card" key={i}>
                <div className="skeleton-bar" style={{ width: '50%', marginBottom: '0.6rem' }} />
                <div className="skeleton-bar" style={{ width: '70%' }} />
              </div>
            ))}
          </div>
        ) : (
          <div className="quick-context">
            <div className="quick-pill">Previous: {formatKg(referenceWeight)}</div>
            <div className="quick-pill">Last updated: {formatDate(summary?.lastMeasuredAt || animal.lastWeightDate)}</div>
            <div className={`quick-pill ${getTrendClass(summary?.trend)}`.trim()}>
              Trend: {toTitle(summary?.trend || animal.weightTrend || 'NO_DATA')}
            </div>
            <div className={`quick-pill ${getTrendClass(liveChange > 0 ? 'GAIN' : liveChange < 0 ? 'LOSS' : 'STABLE')}`.trim()}>
              New change: {renderChangeText(liveChange)}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="wdate">Measurement Date</label>
              <input
                id="wdate"
                type="date"
                className="form-input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="wkg">Weight (kg)</label>
              <input
                id="wkg"
                type="number"
                step="0.1"
                min="0.1"
                className="form-input"
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value)}
                placeholder="e.g., 23.4"
                required
              />
              <div className="helper-text">Enter current measured weight in kilograms. Decimals like 23.4 are supported.</div>
            </div>

            <div className="form-group full">
              <label htmlFor="wnotes">Notes</label>
              <textarea
                id="wnotes"
                className="form-textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional note about growth, feed, illness, treatment, or weighing conditions"
              />
            </div>
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end', gap:'.75rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">
              {isEditing ? 'Update Weight' : 'Save Weight'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const WeightHistoryModal = ({
  isOpen,
  onClose,
  animal,
  summary,
  weights = [],
  loading,
  onEdit,
  onDelete,
  onRefresh,
}) => {
  const rowsDesc = useMemo(() => sortWeightRowsDesc(weights), [weights]);

  if (!isOpen || !animal) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e)=>e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">
              Weight History — {animal.animalId || `#${animal.id}`}
            </div>
            <div className="modal-subtitle">
              {toTitle(animal.type)} • {toTitle(animal.healthStatus)} • {toTitle(animal.currentStatus || (animal.sold ? 'SOLD' : 'AVAILABLE'))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '.6rem' }}>
            <button className="btn btn-secondary" onClick={onRefresh}>Refresh</button>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>

        {loading ? (
          <>
            <div className="summary-strip">
              {[...Array(4)].map((_, i) => (
                <div className="summary-card" key={i}>
                  <div className="skeleton-bar" style={{ width: '45%', marginBottom: '0.6rem' }} />
                  <div className="skeleton-bar" style={{ width: '72%', marginBottom: '0.45rem' }} />
                  <div className="skeleton-bar" style={{ width: '55%' }} />
                </div>
              ))}
            </div>
            <div className="history-layout">
              <div className="panel"><div className="skeleton-bar" style={{ height: 240 }} /></div>
              <div className="panel"><div className="skeleton-bar" style={{ height: 240 }} /></div>
            </div>
          </>
        ) : (
          <>
            <div className="summary-strip">
              <div className="summary-card">
                <div className="summary-label">Current Weight</div>
                <div className="summary-value">{formatKg(summary?.currentWeightKg ?? animal.weight)}</div>
                <div className="summary-meta">
                  Previous: {formatKg(summary?.previousWeightKg)}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-label">Total Gain</div>
                <div className={`summary-value ${getTrendClass(summary?.totalGainKg > 0 ? 'GAIN' : summary?.totalGainKg < 0 ? 'LOSS' : 'STABLE')}`}>
                  {summary?.totalGainKg === null || typeof summary?.totalGainKg === 'undefined'
                    ? '—'
                    : `${summary.totalGainKg > 0 ? '+' : ''}${summary.totalGainKg.toFixed(1)} kg`}
                </div>
                <div className="summary-meta">
                  From initial: {formatKg(summary?.initialWeightKg)}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-label">Last Measured</div>
                <div className="summary-value">{formatDate(summary?.lastMeasuredAt || animal.lastWeightDate)}</div>
                <div className="summary-meta">
                  {summary?.daysSinceLastMeasurement ?? '—'} day(s) since last update
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-label">Records</div>
                <div className="summary-value">{summary?.recordCount ?? weights.length}</div>
                <div className="summary-meta">
                  Trend: {toTitle(summary?.trend || animal.weightTrend || 'NO_DATA')}
                </div>
              </div>
            </div>

            <div className="history-layout">
              <div className="panel">
                <h3 className="panel-title">Growth Trend</h3>
                <WeightLineChart records={weights} />
              </div>

              <div className="panel">
                <h3 className="panel-title">Weight Log</h3>
                <div className="history-table-wrap">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Weight</th>
                        <th>Change</th>
                        <th>Gap</th>
                        <th>Trend</th>
                        <th>Notes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!rowsDesc.length ? (
                        <tr>
                          <td colSpan={7} className="feedback-container">No weight records found.</td>
                        </tr>
                      ) : rowsDesc.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <div className="cell-stack">
                              <span className="primary-text">{formatDate(row.measuredAt)}</span>
                              <span className="secondary-text">{daysAgoLabel(row.measuredAt)}</span>
                            </div>
                          </td>

                          <td className="primary-text">{formatKg(row.weightKg)}</td>

                          <td>
                            <span className={getTrendClass(row.trend)}>
                              {renderChangeText(row.changeFromPreviousKg, row.trend)}
                            </span>
                          </td>

                          <td className="secondary-text">
                            {row.daysSincePrevious == null ? '—' : `${row.daysSincePrevious} day(s)`}
                          </td>

                          <td>
                            <span className={getTrendClass(row.trend)}>
                              {toTitle(row.trend)}
                            </span>
                          </td>

                          <td className="history-note">{row.notes || '—'}</td>

                          <td>
                            <div className="mini-actions">
                              <button className="mini-btn edit" onClick={() => onEdit(row)}><EditIcon /> Edit</button>
                              <button className="mini-btn delete" onClick={() => onDelete(row)}><DeleteIcon /> Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}

        <div style={{display:'flex', justifyContent:'flex-end', marginTop:'1rem'}}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ---------- MAIN ----------
export default function LivestockPage() {
  const { batchId } = useParams();

  const [livestockList, setLivestockList] = useState([]);
  const [selectedLivestockIds, setSelectedLivestockIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isFormOpen, setFormOpen] = useState(false);
  const [editingLivestock, setEditingLivestock] = useState(null);

  const [isWeightOpen, setWeightOpen] = useState(false);
  const [weightAnimal, setWeightAnimal] = useState(null);
  const [weightSummary, setWeightSummary] = useState(null);
  const [weightSummaryLoading, setWeightSummaryLoading] = useState(false);
  const [editingWeightRecord, setEditingWeightRecord] = useState(null);

  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [historyAnimal, setHistoryAnimal] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await livestockService.getByBatchId(batchId);
      setLivestockList(Array.isArray(data) ? data : []);
      setSelectedLivestockIds([]);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err, 'Failed to fetch livestock. Ensure backend is running.'));
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  const fetchWeightDetails = useCallback(async (livestockId) => {
    const [rows, summary] = await Promise.all([
      livestockService.getWeights(livestockId),
      livestockService.getWeightSummary(livestockId),
    ]);

    return {
      rows: Array.isArray(rows) ? rows : [],
      summary: summary || null,
    };
  }, []);

  const save = async (id, data) => {
    try {
      if (id) {
        await livestockService.update(id, { ...data, id });
      } else {
        await livestockService.create(data);
      }
      setFormOpen(false);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this livestock?')) return;
    try {
      await livestockService.delete(id);
      setSelectedLivestockIds((prev) => prev.filter((x) => x !== id));
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    }
  };

  const openCreate = () => {
    setEditingLivestock(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingLivestock(row);
    setFormOpen(true);
  };

  const openWeight = async (row, record = null) => {
    setWeightAnimal(row);
    setEditingWeightRecord(record);
    setWeightSummary(null);
    setWeightSummaryLoading(true);
    setWeightOpen(true);

    try {
      const summary = await livestockService.getWeightSummary(row.id);
      setWeightSummary(summary || null);
    } catch (e) {
      setWeightSummary(null);
    } finally {
      setWeightSummaryLoading(false);
    }
  };

  const closeWeightModal = () => {
    setWeightOpen(false);
    setWeightAnimal(null);
    setWeightSummary(null);
    setEditingWeightRecord(null);
    setWeightSummaryLoading(false);
  };

  const saveWeight = async (livestockId, weightRecordId, payload) => {
    try {
      if (weightRecordId) {
        await livestockService.updateWeight(livestockId, weightRecordId, payload);
      } else {
        await livestockService.addWeight(livestockId, payload);
      }

      closeWeightModal();
      await load();

      if (historyAnimal?.id === livestockId) {
        setHistoryLoading(true);
        try {
          const { rows, summary } = await fetchWeightDetails(livestockId);
          setHistoryRows(rows);
          setHistorySummary(summary);
        } finally {
          setHistoryLoading(false);
        }
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to save weight'));
    }
  };

  const openHistory = async (row) => {
    setHistoryAnimal(row);
    setHistoryRows([]);
    setHistorySummary(null);
    setHistoryLoading(true);
    setHistoryOpen(true);

    try {
      const { rows, summary } = await fetchWeightDetails(row.id);
      setHistoryRows(rows);
      setHistorySummary(summary);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load weight history'));
      setHistoryRows([]);
      setHistorySummary(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setHistoryOpen(false);
    setHistoryAnimal(null);
    setHistoryRows([]);
    setHistorySummary(null);
    setHistoryLoading(false);
  };

  const refreshHistory = async () => {
    if (!historyAnimal?.id) return;
    setHistoryLoading(true);

    try {
      const { rows, summary } = await fetchWeightDetails(historyAnimal.id);
      setHistoryRows(rows);
      setHistorySummary(summary);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to refresh history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleEditWeightRecord = (record) => {
    const animal = historyAnimal;
    closeHistoryModal();
    openWeight(animal, record);
  };

  const handleDeleteWeightRecord = async (record) => {
    if (!historyAnimal?.id) return;
    const ok = window.confirm(`Delete weight record of ${formatKg(record.weightKg)} on ${formatDate(record.measuredAt)}?`);
    if (!ok) return;

    try {
      await livestockService.deleteWeight(historyAnimal.id, record.id);
      await load();
      await refreshHistory();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to delete weight record'));
    }
  };

  const visibleLivestockIds = useMemo(() => livestockList.map((it) => it.id), [livestockList]);
  const allVisibleSelected = visibleLivestockIds.length > 0 && visibleLivestockIds.every((id) => selectedLivestockIds.includes(id));

  const toggleSelectLivestock = (id, checked) => {
    setSelectedLivestockIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAllVisible = (checked) => {
    setSelectedLivestockIds((prev) => {
      if (!checked) return prev.filter((id) => !visibleLivestockIds.includes(id));
      return Array.from(new Set([...prev, ...visibleLivestockIds]));
    });
  };

  const bulkDeleteLivestock = async () => {
    if (!selectedLivestockIds.length) return;
    if (!window.confirm(`Delete ${selectedLivestockIds.length} selected livestock record(s)?`)) return;

    try {
      await livestockService.deleteMany(selectedLivestockIds);
      setSelectedLivestockIds([]);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Bulk delete failed'));
    }
  };

  const bulkMarkSold = async () => {
    if (!selectedLivestockIds.length) return;
    // Record the real sale amount of every animal instead of assuming one price.
    if (!window.confirm(`Record actual selling price for each of ${selectedLivestockIds.length} selected goats?`)) return;
    const updates = [];
    for (const id of selectedLivestockIds) {
      const animal = livestockList.find((row) => row.id === id);
      if (!animal || animal.sold) continue;
      const entered = window.prompt(`Actual sale amount (₹) for ${animal.animalId || `#${id}`}:`, '');
      if (entered === null) return;
      const price = Number(entered);
      if (entered.trim() === '' || !Number.isFinite(price) || price < 0) {
        toast.error('Enter a valid sale amount for every selected goat.');
        return;
      }
      updates.push({ animal, price });
    }
    try {
      await livestockService.recordBulkSales(updates.map(({animal,price}) => ({id: animal.id, saleAmount: price})));
      setSelectedLivestockIds([]);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Bulk mark sold failed'));
    }
  };

  const batchName = livestockList?.[0]?.batchName;

  const headers = [
    '',
    'Animal',
    'Type',
    'Gender',
    'Health',
    'Current Weight',
    'Last Measured',
    'Sale Amount (₹)',
    'Status',
    'Actions',
  ];

  const healthClass = (hs) => {
    const x = (hs || '').toLowerCase();
    return x || 'healthy';
  };

  const statusClass = (it) => {
    const status = (it.currentStatus || (it.sold ? 'SOLD' : 'AVAILABLE')).toLowerCase();
    return ['sold', 'available', 'culled', 'deceased'].includes(status) ? status : 'available';
  };

  const rowBody = () => {
    if (loading) {
      return [...Array(5)].map((_, i) => (
        <tr key={i}>
          {headers.map((h) => (
            <td key={h}>
              <div className="skeleton-bar" style={{width:'80%'}} />
            </td>
          ))}
        </tr>
      ));
    }

    if (error) {
      return (
        <tr>
          <td className="feedback-container" colSpan={headers.length}>{error}</td>
        </tr>
      );
    }

    if (livestockList.length === 0) {
      return (
        <tr>
          <td colSpan={headers.length} className="feedback-container">
            <h3>No livestock found for this batch.</h3>
          </td>
        </tr>
      );
    }

    return livestockList.map((it) => (
      <tr key={it.id}>
        <td>
          <input
            type="checkbox"
            className="select-checkbox"
            checked={selectedLivestockIds.includes(it.id)}
            onChange={(e) => toggleSelectLivestock(it.id, e.target.checked)}
            aria-label={`Select ${it.animalId || `#${it.id}`}`}
          />
        </td>
        <td>
          <div className="cell-stack">
            <span className="primary-text">{it.animalId || `#${it.id}`}</span>
            <span className="secondary-text">Internal ID: #{it.id}</span>
          </div>
        </td>

        <td>{toTitle(it.type)}</td>
        <td>{toTitle(it.gender)}</td>

        <td>
          <span className={`badge ${healthClass(it.healthStatus)}`}>
            {toTitle(it.healthStatus || 'HEALTHY')}
          </span>
        </td>

        <td>
          <div className="cell-stack">
            <span className="weight-main">{safeNumber(it.weight) > 0 ? formatKg(it.weight) : '—'}</span>
            <div className="weight-subline">
              <span className={it.totalWeightGainKg > 0 ? 'weight-trend-up' : it.totalWeightGainKg < 0 ? 'weight-trend-down' : ''} style={{color: it.totalWeightGainKg > 0 ? '#15803d' : it.totalWeightGainKg < 0 ? '#b91c1c' : '#64748b'}}>
                {it.totalWeightGainKg == null ? 'N/A' : `${Number(it.totalWeightGainKg) > 0 ? '+' : ''}${Number(it.totalWeightGainKg).toFixed(1)} kg gained`}
              </span>
              <span className="record-pill">
                {it.weightRecordCount ?? 0} log{(it.weightRecordCount ?? 0) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </td>

        <td>
          <div className="cell-stack">
            <span className="primary-text">{formatDate(it.lastWeightDate)}</span>
            <span className="secondary-text">{daysAgoLabel(it.lastWeightDate)}</span>
          </div>
        </td>

        <td>{it.saleAmount == null ? '—' : formatMoney(it.saleAmount)}</td>

        <td>
          <span className={`badge ${statusClass(it)}`}>
            {toTitle(it.currentStatus || (it.sold ? 'SOLD' : 'AVAILABLE'))}
          </span>
        </td>

        <td className="action-buttons">
          <button className="action-btn weight" title="Record weight" onClick={() => openWeight(it)}>
            <WeightIcon />
          </button>
          <button className="action-btn history" title="Weight history" onClick={() => openHistory(it)}>
            <HistoryIcon />
          </button>
          <button className="action-btn edit" title="Edit livestock" onClick={() => openEdit(it)}>
            <EditIcon />
          </button>
          <button className="action-btn delete" title="Delete livestock" onClick={() => remove(it.id)}>
            <DeleteIcon />
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-container">
      <Styles />

      <div className="page-header">
        <div>
          <div className="breadcrumb">
            <Link to="/admin/batches">Batches</Link>
            <span>/</span>
          </div>
          <h1 className="page-title">
            Livestock for {batchName ? `${batchName} ` : ''}#{batchId}
          </h1>
        </div>

        <div style={{display:'flex', gap:'.75rem'}}>
          <button className="btn btn-secondary" onClick={load}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
            </svg>
            Refresh
          </button>

          <button className="btn btn-primary" onClick={openCreate}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Livestock
          </button>
        </div>
      </div>

      {selectedLivestockIds.length > 0 && (
        <div className="bulk-toolbar">
          <div className="bulk-count">{selectedLivestockIds.length} livestock selected</div>
          <div className="bulk-actions">
            <button className="btn btn-secondary" onClick={bulkMarkSold}>
              <SoldIcon /> Mark as Sold
            </button>
            <button className="btn btn-danger" onClick={bulkDeleteLivestock}>
              <DeleteIcon /> Delete Selected
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>{headers.map((h, idx) => (
              <th key={idx || h}>
                {idx === 0 ? (
                  <input
                    type="checkbox"
                    className="select-checkbox"
                    checked={allVisibleSelected}
                    disabled={!visibleLivestockIds.length}
                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                    aria-label="Select all livestock"
                  />
                ) : h}
              </th>
            ))}</tr>
          </thead>
          <tbody>{rowBody()}</tbody>
        </table>
      </div>

      <LivestockFormModal
        isOpen={isFormOpen}
        onClose={() => setFormOpen(false)}
        onSave={save}
        initialData={editingLivestock}
        batchId={batchId}
        existingLivestock={livestockList}
      />

      <WeightModal
        isOpen={isWeightOpen}
        onClose={closeWeightModal}
        onSave={saveWeight}
        animal={weightAnimal}
        summary={weightSummary}
        loading={weightSummaryLoading}
        initialRecord={editingWeightRecord}
      />

      <WeightHistoryModal
        isOpen={isHistoryOpen}
        onClose={closeHistoryModal}
        animal={historyAnimal}
        summary={historySummary}
        weights={historyRows}
        loading={historyLoading}
        onEdit={handleEditWeightRecord}
        onDelete={handleDeleteWeightRecord}
        onRefresh={refreshHistory}
      />
    </div>
  );
}
